(() => {
  if (window.Probieraktion && window.Probieraktion.ready) return;

  const state = (window.Probieraktion = window.Probieraktion || {});
  state.ready = true;
  state.mode = state.mode || 'abo';
  state.hrefs = state.hrefs || {};

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const baseProps = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      probieraktion_seite: state.page || null,
      probieraktion_variante: state.variant || null,
      modus: state.mode,
      utm_content: params.get('utm_content'),
    };
  };

  const track = (event, props = {}) => {
    const payload = Object.assign(baseProps(), props);
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(event, payload);
      }
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, payload);
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event }, payload));
    } catch (error) {
      /* Tracking darf die Seite nie blockieren */
    }
  };

  const setModeVisibility = (root, mode) => {
    root.querySelectorAll('[data-mode-show]').forEach((element) => {
      element.hidden = element.dataset.modeShow !== mode;
    });
  };

  // Schwebende Symbole anderer Apps (z. B. Cookie-Einstellungen) sollen Preis und Knopf der Leiste nicht verdecken
  const avoidFloatingIcons = (bar) => {
    let left = false;
    let right = false;
    document.querySelectorAll('.cc-revoke').forEach((icon) => {
      const rect = icon.getBoundingClientRect();
      const style = getComputedStyle(icon);
      if (!rect.width || style.visibility === 'hidden' || style.opacity === '0') return;
      if (rect.bottom < window.innerHeight - bar.offsetHeight) return;
      if (rect.left + rect.width / 2 < window.innerWidth / 2) {
        left = true;
      } else {
        right = true;
      }
    });
    bar.classList.toggle('probieraktion-stickybar--avoid-left', left);
    bar.classList.toggle('probieraktion-stickybar--avoid-right', right);
  };

  const initStickyBar = (bar) => {
    if (!bar || !('IntersectionObserver' in window)) return null;

    const visibleTargets = new Set();
    let ready = false;
    // Erst nach dem ersten Runterscrollen zeigen, nicht schon beim Laden ganz oben
    const scrollThreshold = 80;
    let scrolled = window.scrollY > scrollThreshold;

    const update = () => {
      const show = ready && scrolled && visibleTargets.size === 0 && !bar.hidden;
      if (show) avoidFloatingIcons(bar);
      bar.classList.toggle('is-visible', show);
      bar.toggleAttribute('inert', !show);
      bar.setAttribute('aria-hidden', String(!show));
      // Fokussierte Elemente sollen nicht unter der Leiste verschwinden (WCAG 2.4.11)
      const height = show ? bar.offsetHeight : 0;
      document.documentElement.style.scrollPaddingBottom = height ? `${height}px` : '';
    };
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener(
      'scroll',
      () => {
        const next = window.scrollY > scrollThreshold;
        if (next !== scrolled) {
          scrolled = next;
          update();
        }
      },
      { passive: true }
    );

    const headerHeight =
      parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleTargets.add(entry.target);
          } else {
            visibleTargets.delete(entry.target);
          }
        });
        ready = true;
        update();
      },
      { rootMargin: `-${headerHeight}px 0px 0px 0px` }
    );

    document
      .querySelectorAll('[data-probieraktion-primary], [data-probieraktion-hide-bar], footer')
      .forEach((target) => observer.observe(target));

    update();
    return observer;
  };

  // Bestell-Attribute für die Auswertung (Seite, Variante, Flyer-Version) an der Bestellung
  const orderAttributes = () => {
    const attributes = { probieraktion: `${state.page || 'seite'}_${state.variant || 'standard'}_${state.mode}` };
    const utmContent = new URLSearchParams(window.location.search).get('utm_content');
    if (utmContent) attributes.utm_content = utmContent;
    return attributes;
  };

  // Warenkorb-Links (/cart/VARIANTE:MENGE) bekommen die Attribute direkt in der URL
  const withCartAttributes = (link) => {
    const href = link.getAttribute('href') || '';
    if (!/^\/cart\/\d+:\d+/.test(href) || href.includes('attributes')) return;
    const url = new URL(href, window.location.origin);
    Object.entries(orderAttributes()).forEach(([key, value]) => url.searchParams.set(`attributes[${key}]`, value));
    link.setAttribute('href', url.pathname + url.search);
  };

  // Seal-Kasse-Links leeren nur die Artikel, daher Attribute vorher per Ajax setzen (max. 800 ms warten)
  const isSealLink = (href) => /^\/a\/subscriptions\//.test(href || '');
  const goWithAttributes = (link, href) => {
    link.setAttribute('aria-busy', 'true');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const controller = 'AbortController' in window ? new AbortController() : null;
    const timer = window.setTimeout(() => controller && controller.abort(), 800);
    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: orderAttributes() }),
      signal: controller ? controller.signal : undefined,
    })
      .catch(() => {})
      .finally(() => {
        window.clearTimeout(timer);
        window.location.href = href;
      });
  };

  // Cookie-Einstellungen: Der schwebende Pandectes-Knopf ist auf den Probieraktion-Seiten ausgeblendet (CSS),
  // stattdessen öffnet der Footer-Link die Einstellungen. Link nur zeigen, wenn Pandectes verfügbar ist.
  const cookieApi = () => window.Pandectes && window.Pandectes.fn && typeof window.Pandectes.fn.openPreferences === 'function';
  const showCookieLinks = () => {
    if (!cookieApi()) return false;
    const items = document.querySelectorAll('[data-probieraktion-cookie-item]');
    if (!items.length) return true;
    items.forEach((item) => {
      item.hidden = false;
    });
    // Erst jetzt den schwebenden Knopf ausblenden: Der Ersatz-Link ist sichtbar und funktioniert
    document.body.classList.add('probieraktion-cookie-link');
    return true;
  };
  if (!showCookieLinks()) {
    // Pandectes kann spät laden: weiter prüfen, nach 10 Sekunden seltener, bis die Schnittstelle da ist
    let tries = 0;
    const check = () => {
      if (showCookieLinks()) return;
      tries += 1;
      setTimeout(check, tries < 40 ? 250 : 2000);
    };
    setTimeout(check, 250);
  }
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-probieraktion-cookie-settings]');
    if (!button || !cookieApi()) return;
    window.Pandectes.fn.openPreferences();
  });

  // Bild-Popups (custom-probieraktion-popup): Links auf #<Auslöser> öffnen das passende <dialog>
  document.addEventListener('click', (event) => {
    const closer = event.target.closest('[data-probieraktion-dialog-close]');
    if (closer) {
      closer.closest('dialog').close();
      return;
    }

    const openDialog = event.target.closest('dialog[open][data-probieraktion-dialog]');
    if (openDialog && event.target === openDialog) {
      openDialog.close();
      return;
    }

    const link = event.target.closest('a[href^="#"]');
    if (!link) return;
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const dialog = id && document.querySelector(`dialog[data-probieraktion-dialog="${CSS.escape(id)}"]`);
    // Sprungmarke aus einem offenen Popup heraus (z. B. Knopf „Startpaket sichern“): Popup schließen, dann normal springen
    const parentDialog = link.closest('dialog[open][data-probieraktion-dialog]');
    if (parentDialog && parentDialog !== dialog) parentDialog.close();
    const quelle = link.dataset.probieraktionQuelle || null;
    if (!dialog || typeof dialog.showModal !== 'function') {
      // Sprung innerhalb der Seite aus Angebotszeile, Bild oder Kategorie (z. B. #beratung)
      const target = quelle && id && document.getElementById(id);
      const plain = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
      if (target && plain) {
        event.preventDefault();
        target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
        target.focus({ preventScroll: true });
        if (history.replaceState) history.replaceState(null, '', `#${id}`);
        track('probieraktion_sprung', { ziel: id, quelle });
      }
      return;
    }
    event.preventDefault();
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    dialog.addEventListener(
      'close',
      () => {
        // Nur entsperren, wenn kein anderes Popup mehr offen ist (Wechsel von Popup A zu B)
        if (!document.querySelector('dialog[open][data-probieraktion-dialog]')) {
          document.documentElement.style.removeProperty('overflow');
        }
      },
      { once: true }
    );
    track('probieraktion_popup_geoeffnet', { popup: id, quelle });
  });

  // Links, die ein Popup öffnen, für Screenreader kennzeichnen
  const markDialogLinks = () => {
    document.querySelectorAll('.probieraktion a[href^="#"]').forEach((link) => {
      const id = decodeURIComponent(link.getAttribute('href').slice(1));
      if (id && document.querySelector(`dialog[data-probieraktion-dialog="${CSS.escape(id)}"]`)) {
        link.setAttribute('aria-haspopup', 'dialog');
      }
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', markDialogLinks);
  } else {
    markDialogLinks();
  }
  document.addEventListener('shopify:section:load', markDialogLinks);

  // Popup-Bild vorladen, sobald der Link berührt oder fokussiert wird
  const preloadDialog = (event) => {
    const link = event.target.closest && event.target.closest('a[href^="#"]');
    if (!link) return;
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const dialog = id && document.querySelector(`dialog[data-probieraktion-dialog="${CSS.escape(id)}"]`);
    const image = dialog && dialog.querySelector('img[loading="lazy"]');
    if (image) image.loading = 'eager';
  };
  document.addEventListener('pointerover', preloadDialog);
  document.addEventListener('focusin', preloadDialog);
  document.addEventListener('touchstart', preloadDialog, { passive: true });

  // Wisch-Reihen (z. B. Inhaltsstoff-Kategorien) nur dann per Tastatur fokussierbar machen, wenn sie wirklich scrollen
  const updateScrollers = () => {
    document.querySelectorAll('[data-probieraktion-scroller]').forEach((element) => {
      if (element.scrollWidth > element.clientWidth + 1) {
        element.setAttribute('tabindex', '0');
      } else {
        element.removeAttribute('tabindex');
      }
    });
  };
  window.addEventListener('resize', updateScrollers, { passive: true });
  document.addEventListener('shopify:section:load', updateScrollers);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateScrollers);
  } else {
    updateScrollers();
  }

  // Nach „Zurück“ aus der Kasse (Back-Forward-Cache) wieder klickbar machen
  window.addEventListener('pageshow', () => {
    state.redirecting = false;
    document.querySelectorAll('[data-probieraktion-cta][aria-busy]').forEach((link) => link.removeAttribute('aria-busy'));
  });

  document.addEventListener('click', (event) => {
    const cta = event.target.closest('[data-probieraktion-cta]');
    if (!cta) return;
    const plainClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    if (cta.tagName === 'A') withCartAttributes(cta);
    track('probieraktion_cta_klick', {
      position: cta.dataset.probieraktionCta,
      modus: cta.dataset.modeShow || state.mode,
      ziel: cta.getAttribute('href'),
    });
    if (cta.tagName === 'A' && plainClick && isSealLink(cta.getAttribute('href')) && typeof window.fetch === 'function') {
      event.preventDefault();
      if (state.redirecting) return;
      state.redirecting = true;
      goWithAttributes(cta, cta.getAttribute('href'));
    }
  });

  // Video unter dem Angebot (custom-probieraktion-video). Die Fassungen liegen als <template> vor; eingesetzt wird
  // nur die sichtbare, damit Handys weder Desktop-Video noch dessen Vorschaubild laden.
  const videoFrames = () => document.querySelectorAll('[data-probieraktion-video]');
  const mountVideos = () => {
    videoFrames().forEach((frame) => {
      const visible = getComputedStyle(frame).display !== 'none';
      const video = frame.querySelector('video');
      if (!visible) {
        if (video && !video.paused) video.pause();
        return;
      }
      const template = frame.querySelector('template');
      if (!video && template) {
        frame.appendChild(template.content.cloneNode(true));
        // Bedienelemente erst nach dem Start: vorher liegt der große Abspielknopf darüber
        const mounted = frame.querySelector('video');
        if (mounted && frame.querySelector('[data-probieraktion-video-play]')) mounted.controls = false;
      }
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountVideos);
  } else {
    mountVideos();
  }
  const videoBreakpoint = window.matchMedia('(min-width: 750px)');
  if (typeof videoBreakpoint.addEventListener === 'function') videoBreakpoint.addEventListener('change', mountVideos);
  document.addEventListener('shopify:section:load', mountVideos);

  // Großer Abspielknopf: setzt die Fassung bei Bedarf ein und startet sie; bei jedem Abspielen (auch über die
  // eigenen Bedienelemente) verschwindet er
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-probieraktion-video-play]');
    if (!button) return;
    const frame = button.closest('[data-probieraktion-video]');
    if (!frame) return;
    let video = frame.querySelector('video');
    const template = frame.querySelector('template');
    if (!video && template) {
      frame.appendChild(template.content.cloneNode(true));
      video = frame.querySelector('video');
    }
    if (!video) return;
    frame.classList.add('is-started');
    video.controls = true;
    if (!video.hasAttribute('tabindex')) video.tabIndex = -1;
    track('probieraktion_video_knopf', { fassung: frame.dataset.probieraktionVideo });
    const playing = video.play();
    if (playing && typeof playing.catch === 'function') {
      playing.catch(() => {
        // Abspielen verweigert (z. B. Energiesparmodus): Bedienelemente des Browsers übernehmen
      });
    }
    video.focus({ preventScroll: true });
  });

  // Sichtbarkeit: einmal je Fassung, sobald die Hälfte des Videos im Bild ist (Anteil der Besucher, die es sehen)
  const watchVideoVisibility = () => {
    if (typeof IntersectionObserver !== 'function') return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          const fassung = entry.target.dataset.probieraktionVideo;
          trackVideoOnce({ fassung }, 'sichtbar', 'probieraktion_video_sichtbar');
        });
      },
      { threshold: 0.5 }
    );
    videoFrames().forEach((frame) => {
      if (!frame.dataset.probieraktionVideoObserved) {
        frame.dataset.probieraktionVideoObserved = 'true';
        observer.observe(frame);
      }
    });
  };

  // Melden: Start, angesehener Anteil (25/50/75 %, nur tatsächlich abgespielte Zeit, Springen zählt nicht)
  // und Ende, je Fassung einmal pro Seitenaufruf
  const videoMarks = {};
  const videoWatch = new WeakMap();
  const videoProps = (video) => {
    const frame = video.tagName === 'VIDEO' && video.closest('[data-probieraktion-video]');
    return frame ? { fassung: frame.dataset.probieraktionVideo, dauer_s: Math.round(video.duration) || null } : null;
  };
  const trackVideoOnce = (props, mark, event, extra) => {
    const key = `${props.fassung}:${mark}`;
    if (videoMarks[key]) return;
    videoMarks[key] = true;
    track(event, Object.assign({}, props, extra));
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchVideoVisibility);
  } else {
    watchVideoVisibility();
  }
  document.addEventListener('shopify:section:load', watchVideoVisibility);

  document.addEventListener(
    'play',
    (event) => {
      const props = videoProps(event.target);
      if (!props) return;
      const ownFrame = event.target.closest('[data-probieraktion-video]');
      if (ownFrame) ownFrame.classList.add('is-started');
      event.target.controls = true;
      videoFrames().forEach((frame) => {
        const other = frame.querySelector('video');
        if (other && other !== event.target && !other.paused) other.pause();
      });
    },
    true
  );
  // Erst bei „playing“ ist die Dauer bekannt (preload none)
  document.addEventListener(
    'playing',
    (event) => {
      const props = videoProps(event.target);
      if (props) trackVideoOnce(props, 'start', 'probieraktion_video_start');
    },
    true
  );
  document.addEventListener(
    'timeupdate',
    (event) => {
      const video = event.target;
      const props = video.duration && videoProps(video);
      if (!props) return;
      const watch = videoWatch.get(video) || { last: video.currentTime, seconds: 0 };
      const step = video.currentTime - watch.last;
      if (!video.paused && !video.seeking && !watch.jumped && step > 0 && step < 2) watch.seconds += step;
      watch.jumped = false;
      watch.last = video.currentTime;
      videoWatch.set(video, watch);
      const percent = (watch.seconds / video.duration) * 100;
      [25, 50, 75].forEach((mark) => {
        if (percent >= mark) trackVideoOnce(props, mark, 'probieraktion_video_fortschritt', { prozent: mark });
      });
    },
    true
  );
  // Nach einem Sprung zählt erst die nächste Abspielstrecke
  document.addEventListener(
    'seeking',
    (event) => {
      const watch = videoWatch.get(event.target);
      if (watch) watch.jumped = true;
    },
    true
  );
  document.addEventListener(
    'ended',
    (event) => {
      const props = videoProps(event.target);
      if (props) trackVideoOnce(props, 100, 'probieraktion_video_fortschritt', { prozent: 100 });
    },
    true
  );

  // Countdown bis zu Datum und Uhrzeit in deutscher Zeit (data-end-date JJJJ-MM-TT, data-end-time HH:MM).
  // onTick(datum, rest) bei jeder geänderten Anzeige, onEnd() einmal nach Ablauf.
  const startCountdown = (element, { onTick, onEnd }) => {
    const [year, month, day] = (element.dataset.endDate || '').split('-').map(Number);
    const [hour, minute] = (element.dataset.endTime || '23:59').split(':').map(Number);
    if (!year || !month || !day) return null;

    // Berliner Ortszeit in UTC umrechnen. Der Versatz wird am tatsächlichen Zielzeitpunkt erneut bestimmt,
    // damit die Frist auch am Tag der Zeitumstellung stimmt.
    const berlinFormat = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Berlin',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    const berlinOffset = (timestamp) => {
      const parts = berlinFormat.formatToParts(new Date(timestamp));
      const part = (type) => Number(parts.find((entry) => entry.type === type).value);
      return Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute')) - timestamp;
    };
    const guess = Date.UTC(year, month - 1, day, hour || 0, minute || 0);
    const firstPass = guess - berlinOffset(guess);
    const end = guess - berlinOffset(firstPass);

    const lang = document.documentElement.lang || 'de';
    const dateLabel = new Intl.DateTimeFormat(lang, {
      weekday: 'short',
      day: 'numeric',
      month: 'numeric',
      timeZone: 'Europe/Berlin',
    }).format(new Date(end));
    const unit = (value, name, display) =>
      new Intl.NumberFormat(lang, { style: 'unit', unit: name, unitDisplay: display }).format(value);

    let timer = null;
    let lastRest = null;
    const stop = () => window.clearInterval(timer);
    const render = () => {
      const left = end - Date.now();
      if (left <= 0) {
        stop();
        onEnd();
        return;
      }
      const minutes = Math.max(1, Math.floor(left / 60000));
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      let rest = unit(minutes % 60, 'minute', 'short');
      if (days > 0) {
        rest = `${unit(days, 'day', 'long')} ${unit(hours, 'hour', 'short')}`;
      } else if (hours > 0) {
        rest = `${unit(hours, 'hour', 'short')} ${rest}`;
      }
      // Nur bei geänderter Anzeige neu schreiben
      if (rest === lastRest) return;
      lastRest = rest;
      onTick(dateLabel, rest);
    };

    render();
    if (end - Date.now() > 0) timer = window.setInterval(render, 30000);
    return { stop };
  };

  // Schreibt eine Vorlage wie „Nur bis {datum} · noch {rest}“ in das Ziel; der Rest erscheint fett
  const fillCountdown = (target, template, dateLabel, rest) => {
    if (!target) return;
    target.replaceChildren();
    template.split(/(\{datum\}|\{rest\})/).forEach((piece) => {
      if (piece === '{rest}') {
        const strong = document.createElement('strong');
        strong.textContent = rest;
        target.append(strong);
      } else {
        target.append(piece === '{datum}' ? dateLabel : piece);
      }
    });
  };

  if (!customElements.get('probieraktion-angebot')) {
    customElements.define(
      'probieraktion-angebot',
      class ProbieraktionAngebot extends HTMLElement {
        connectedCallback() {
          this.oneTimeEnabled = this.dataset.oneTime === 'true';
          state.page = 'kauf';
          state.variant = this.oneTimeEnabled ? 'abo_und_einmal' : 'nur_abo';

          ['abo', 'einmal'].forEach((mode) => {
            const link = this.querySelector(`[data-probieraktion-cta="angebot"][data-mode-show="${mode}"]`);
            if (link) state.hrefs[mode] = link.getAttribute('href');
          });

          this.querySelectorAll('[data-probieraktion-set-mode]').forEach((button) => {
            button.addEventListener('click', () => this.setMode(button.dataset.probieraktionSetMode, true));
          });

          const requested = new URLSearchParams(window.location.search).get('angebot');
          this.setMode(requested === 'einmal' ? 'einmal' : 'abo', false);

          this.stickyObserver = initStickyBar(this.querySelector('[data-probieraktion-stickybar]'));
          this.initOfferCountdown();
          track('probieraktion_seitenaufruf');
        }

        disconnectedCallback() {
          if (this.stickyObserver) this.stickyObserver.disconnect();
          if (this.countdown) this.countdown.stop();
        }

        // Echte Angebotsfrist: Countdown in Karte und Kaufleiste, danach Hinweis statt Kaufknöpfen
        initOfferCountdown() {
          const element = this.querySelector('[data-probieraktion-countdown]');
          if (!element) return;
          const target = element.querySelector('[data-probieraktion-countdown-text]');
          const template = element.dataset.text || '{datum} · {rest}';
          const short = this.querySelector('[data-probieraktion-countdown-short]');
          this.countdown = startCountdown(element, {
            onTick: (dateLabel, rest) => {
              fillCountdown(target, template, dateLabel, rest);
              element.hidden = false;
              if (short) {
                fillCountdown(short.querySelector('[data-probieraktion-countdown-text]'), short.dataset.text || '{rest}', dateLabel, rest);
                short.hidden = false;
              }
            },
            onEnd: () => this.endOffer(element),
          });
        }

        endOffer(countdown) {
          if (this.countdown) this.countdown.stop();
          countdown.hidden = true;
          const ended = this.querySelector('[data-probieraktion-ended]');
          if (ended) ended.hidden = false;
          // Nach Ablauf kein Angebot mehr zeigen: neutrale Überschrift, keine Geschenke, keine Kaufknöpfe
          const heading = this.querySelector('[data-probieraktion-heading]');
          if (heading && heading.dataset.endedHeading) heading.textContent = heading.dataset.endedHeading;
          document
            .querySelectorAll(
              '[data-probieraktion-cta], [data-probieraktion-set-mode], [data-probieraktion-countdown-short], .probieraktion-offer__guarantee, .probieraktion-offer__facts, .probieraktion-offer__payments, .probieraktion-offer__header, .probieraktion-offer__items, .probieraktion-offer__note, .probieraktion-offer__total, .probieraktion__text, .probieraktion-hero__counter'
            )
            .forEach((element) => {
              element.hidden = true;
            });
          const bar = this.querySelector('[data-probieraktion-stickybar]');
          if (bar) {
            bar.hidden = true;
            bar.classList.remove('is-visible');
          }
          document.documentElement.style.scrollPaddingBottom = '';
          track('probieraktion_angebot_abgelaufen');
        }

        setMode(requestedMode, userAction) {
          const mode = requestedMode === 'einmal' && this.oneTimeEnabled ? 'einmal' : 'abo';
          state.mode = mode;
          this.dataset.mode = mode;
          setModeVisibility(this, mode);

          document.dispatchEvent(
            new CustomEvent('probieraktion:mode', { detail: { mode, href: state.hrefs[mode] || null } })
          );

          if (!userAction) return;

          const target = this.querySelector(`[data-probieraktion-set-mode]:not([hidden])`);
          if (target) target.focus();
          track('probieraktion_modus_gewechselt');
        }
      }
    );
  }

  if (!customElements.get('probieraktion-anmeldung')) {
    customElements.define(
      'probieraktion-anmeldung',
      class ProbieraktionAnmeldung extends HTMLElement {
        connectedCallback() {
          this.formId = this.dataset.formId;
          this.formMode = this.dataset.formMode;
          this.formTarget = this.querySelector('[data-probieraktion-form-target]');
          state.page = 'anmeldung';
          state.variant = this.formMode;

          this.onDocumentClick = this.onDocumentClick.bind(this);
          this.onKlaviyoForm = this.onKlaviyoForm.bind(this);
          document.addEventListener('click', this.onDocumentClick);
          window.addEventListener('klaviyoForms', this.onKlaviyoForm);

          this.watchEmbed();
          this.stickyObserver = initStickyBar(this.querySelector('[data-probieraktion-stickybar]'));
          this.initCountdown();
          track('probieraktion_seitenaufruf');
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocumentClick);
          window.removeEventListener('klaviyoForms', this.onKlaviyoForm);
          window.clearTimeout(this.fallbackTimer);
          window.clearTimeout(this.popupTimer);
          if (this.countdown) this.countdown.stop();
          window.cancelAnimationFrame(this.enhanceFrame);
          if (this.embedObserver) this.embedObserver.disconnect();
          if (this.stickyObserver) this.stickyObserver.disconnect();
        }

        // Countdown bis zum Ende der Aktion (deutsche Zeit); danach erscheint statt des Formulars der Hinweistext
        initCountdown() {
          const element = this.querySelector('[data-probieraktion-countdown]');
          if (!element) return;
          const target = element.querySelector('[data-probieraktion-countdown-text]');
          const template = element.dataset.text || '{datum} · {rest}';
          this.countdown = startCountdown(element, {
            onTick: (dateLabel, rest) => {
              fillCountdown(target, template, dateLabel, rest);
              element.hidden = false;
            },
            onEnd: () => this.endCampaign(element),
          });
        }

        endCampaign(countdown) {
          if (this.countdown) this.countdown.stop();
          this.ended = true;
          const ended = this.querySelector('[data-probieraktion-ended]');
          if (!ended) {
            countdown.hidden = true;
            return;
          }
          countdown.hidden = true;
          ended.hidden = false;
          this.querySelectorAll(
            '[data-probieraktion-form], [data-probieraktion-form-loading], [data-probieraktion-fallback], [data-probieraktion-open-form], [data-probieraktion-form-note], [data-probieraktion-countdown-note]'
          ).forEach((element) => {
            element.hidden = true;
          });
          const bar = this.querySelector('[data-probieraktion-stickybar]');
          if (bar) {
            bar.hidden = true;
            bar.classList.remove('is-visible');
          }
          document.documentElement.style.scrollPaddingBottom = '';
          track('probieraktion_aktion_beendet');
        }

        onKlaviyoForm(event) {
          const detail = event.detail || {};
          if (this.formId && detail.formId && detail.formId !== this.formId) return;
          if (detail.type === 'embedOpen' || detail.type === 'open') {
            this.popupOpened = true;
            this.setFallback(false);
          }
          // submit = erster Schritt mit E-Mail, stepSubmit = jeder weitere Schritt, redirectedToUrl = Abschluss
          if (detail.type === 'submit') {
            if (detail.metaData) this.lastEmail = detail.metaData.email || detail.metaData.$email || this.lastEmail;
            track('probieraktion_anmeldung_abgeschickt', { formular: detail.formId });
          }
          if (detail.type === 'stepSubmit') {
            track('probieraktion_formular_schritt', { formular: detail.formId, schritt: detail.metaData && detail.metaData.$step_name });
            this.backupFormData(detail);
            this.saveStepToKlaviyo(detail);
          }
          if (detail.type === 'redirectedToUrl') track('probieraktion_anmeldung_abgeschlossen', { formular: detail.formId });
          if (detail.type === 'open') track('probieraktion_formular_geoeffnet', { formular: detail.formId });
        }

        // Sicherungskopie der Formulardaten an PostHog, falls die Anfrage an Klaviyo unterwegs verloren geht.
        // Direkter Aufruf ohne Cookies, damit sie auch bei abgelehnten Cookies ankommt (vom Datenschutzbeauftragten
        // freigegeben, 08.10.2026). Nur an PostHog, nicht an dataLayer oder Shopify-Analytics.
        backupFormData(detail) {
          try {
            const config = window.posthog && window.posthog.config;
            const data = detail.metaData || {};
            const email = data.email || data.$email || this.lastEmail;
            if (!config || !config.token || !config.api_host || !email) return;
            const properties = {
              formular: detail.formId,
              schritt: data.$step_name,
              $current_url: window.location.href,
              $process_person_profile: false,
            };
            Object.entries(data).forEach(([key, value]) => {
              if (value === null || value === undefined || typeof value === 'object' && !Array.isArray(value)) return;
              properties[`feld_${key.replace(/^\$/, '')}`] = Array.isArray(value) ? value.join(', ') : value;
            });
            const body = JSON.stringify({
              api_key: config.token,
              event: 'probieraktion_formulardaten',
              distinct_id: email,
              properties,
              timestamp: new Date().toISOString(),
            });
            // text/plain ohne Preflight, ausdrücklich ohne Cookies
            fetch(`${config.api_host.replace(/\/$/, '')}/i/v0/e/`, {
              method: 'POST',
              body,
              keepalive: true,
              credentials: 'omit',
              headers: { 'Content-Type': 'text/plain' },
            }).catch(() => {});
          } catch (error) {
            /* Die Sicherung darf das Formular nie blockieren */
          }
        }

        // Weitere Formularschritte speichert Klaviyo über ein Skript von static-tracking.klaviyo.com, das Tracking-Schutz
        // (Werbeblocker, Safari, Samsung Internet) oft blockiert; dann fehlt die Adresse in Klaviyo (09.10.2026: etwa
        // jede vierte Anmeldung). Deshalb schickt das Theme diese Schritte zusätzlich selbst an Klaviyos öffentliche
        // Client-API auf a.klaviyo.com, über die auch Schritt 1 ankommt. Gleiche Felder wie das Formular.
        saveStepToKlaviyo(detail) {
          try {
            const data = detail.metaData || {};
            // Schritt 1 mit der E-Mail speichert Klaviyo selbst (client/subscriptions, nicht blockiert)
            if (!this.lastEmail || data.email || data.$email) return;
            // Öffentlicher Klaviyo-Schlüssel; nicht das erste Klaviyo-Skript nehmen (das ist oft datadome.js ohne Schlüssel)
            const script = [...document.scripts].map((element) => element.src).find((src) => /klaviyo\.com\/onsite\/js\/\w+\/klaviyo\.js|klaviyo\.js\?.*company_id=/.test(src)) || '';
            const company = (window.klaviyoModulesObject && window.klaviyoModulesObject.companyId)
              || (script.match(/onsite\/js\/(\w+)\/klaviyo\.js/) || script.match(/company_id=(\w+)/) || [])[1];
            if (!company) return;
            const attributes = { email: this.lastEmail };
            const location = {};
            const properties = {};
            Object.entries(data).forEach(([key, value]) => {
              if (key === '$step_name' || value === null || value === undefined || value === '') return;
              if (typeof value === 'object' && !Array.isArray(value)) return;
              const field = key.replace(/^\$/, '');
              if (['first_name', 'last_name'].includes(field)) attributes[field] = value;
              else if (['address1', 'address2', 'zip', 'city', 'region', 'country'].includes(field)) location[field] = value;
              else if (!key.startsWith('$')) properties[key] = value;
            });
            if (Object.keys(location).length) attributes.location = location;
            if (Object.keys(properties).length) attributes.properties = properties;
            fetch(`https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(company)}`, {
              method: 'POST',
              credentials: 'omit',
              headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
              body: JSON.stringify({ data: { type: 'profile', attributes } }),
            })
              .then((response) => track('probieraktion_klaviyo_sicherung', { formular: detail.formId, status: response.status }))
              .catch(() => track('probieraktion_klaviyo_sicherung', { formular: detail.formId, status: 0 }));
          } catch (error) {
            /* Die Sicherung darf das Formular nie blockieren */
          }
        }

        setFallback(show) {
          if (this.ended) return;
          const fallback = this.querySelector('[data-probieraktion-fallback]');
          if (fallback) fallback.hidden = !show;
        }

        // Klaviyo setzt für Adressfelder kein autocomplete; das ergänzt das Theme für Autofill.
        // Keine Zifferntastatur bei der PLZ: Die Probe geht auch ins Ausland, dort enthalten PLZ Buchstaben.
        enhanceInputs(container) {
          const hints = [
            [/first_name|vorname/i, 'given-name'],
            [/last_name|nachname/i, 'family-name'],
            [/address1|stra(ß|ss)e/i, 'address-line1'],
            [/zipcode|\bzip\b|\$zip|postleitzahl|\bplz\b/i, 'postal-code'],
            [/\bcity\b|\$city|stadt|\bort\b/i, 'address-level2'],
            [/country|\bland\b/i, 'country-name'],
          ];
          container.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"]), select').forEach((input) => {
            if (input.type === 'email' && !input.getAttribute('autocomplete')) input.setAttribute('autocomplete', 'email');
            const key = [input.name, input.id, input.placeholder, input.getAttribute('aria-label')].join(' ');
            const hint = hints.find(([pattern]) => pattern.test(key));
            if (!hint) return;
            if (!input.getAttribute('autocomplete') || input.getAttribute('autocomplete') === 'off') {
              input.setAttribute('autocomplete', hint[1]);
            }
            if (hint[2] && !input.getAttribute('inputmode')) input.setAttribute('inputmode', hint[2]);
          });
        }

        // Bis Klaviyo das Formular einsetzt, zeigt die Karte einen Platzhalter; nach 8 s ohne Formular den Hinweis
        // mit „Formular neu laden“. Kommt das Formular später doch, ersetzt es Platzhalter und Hinweis.
        watchEmbed() {
          const container = this.querySelector('[data-probieraktion-form]');
          if (!container) return;
          const loading = this.querySelector('[data-probieraktion-form-loading]');
          // Erst ein tatsächlich sichtbares Eingabefeld zählt: Klaviyo setzt teils vorher leere Hüllen
          // und ein verstecktes Absende-Feld ein
          const fieldSelector =
            'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="image"]):not([type="reset"]), select, textarea';
          const formReady = () =>
            [...container.querySelectorAll(fieldSelector)].some((field) => field.getClientRects().length > 0);
          const markReady = () => {
            if (this.formReady) return;
            this.formReady = true;
            window.clearTimeout(this.fallbackTimer);
            if (loading) loading.hidden = true;
            this.setFallback(false);
            if (this.fallbackShownAt) {
              track('probieraktion_formular_spaet_geladen', {
                formular: this.formId,
                sekunden: Math.round((Date.now() - this.pageStart) / 1000),
              });
            }
          };

          this.pageStart = Date.now();
          this.embedObserver = new MutationObserver(() => {
            if (!this.formReady && formReady()) markReady();
            window.cancelAnimationFrame(this.enhanceFrame);
            this.enhanceFrame = window.requestAnimationFrame(() => this.enhanceInputs(container));
          });
          // Auch Sichtbarkeitswechsel beobachten (Klaviyo blendet eingesetzte Formulare teils erst später ein)
          this.embedObserver.observe(container, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class', 'hidden'],
          });
          if (formReady()) {
            markReady();
            return;
          }

          this.fallbackTimer = window.setTimeout(() => {
            // Einmal geladen bleibt geladen, auch wenn der Danke-Schritt keine Eingabefelder mehr hat
            if (this.formReady) return;
            if (formReady()) return markReady();
            if (loading) loading.hidden = true;
            this.fallbackShownAt = Date.now();
            this.setFallback(true);
            track('probieraktion_formular_nicht_geladen', { formular: this.formId });
          }, 8000);
        }

        onDocumentClick(event) {
          const reload = event.target.closest('[data-probieraktion-form-reload]');
          if (reload && this.contains(reload)) {
            reload.disabled = true;
            track('probieraktion_formular_neu_laden', { formular: this.formId });
            // kurz warten, damit das Ereignis vor dem Neuladen rausgeht
            window.setTimeout(() => window.location.reload(), 200);
            return;
          }
          const trigger = event.target.closest('[data-probieraktion-open-form], a[href="#anmeldung"]');
          if (!trigger) return;
          event.preventDefault();
          this.openForm();
        }

        openForm() {
          if (this.formMode === 'popup' && this.formId) {
            window._klOnsite = window._klOnsite || [];
            window._klOnsite.push(['openForm', this.formId]);
            // Öffnet sich das Popup nicht (z. B. Klaviyo blockiert), erscheint der Hinweistext in der Karte
            window.clearTimeout(this.popupTimer);
            this.popupOpened = false;
            this.popupTimer = window.setTimeout(() => {
              if (this.popupOpened) return;
              this.setFallback(true);
              if (this.formTarget) this.formTarget.scrollIntoView({ block: 'center' });
              track('probieraktion_formular_nicht_geladen', { formular: this.formId });
            }, 3000);
            return;
          }

          if (!this.formTarget) return;
          this.formTarget.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });

          window.setTimeout(() => {
            const field = this.formTarget.querySelector(
              'input:not([type="hidden"]):not([disabled]), select, textarea'
            );
            if (field) {
              field.focus({ preventScroll: true });
            } else {
              this.formTarget.focus({ preventScroll: true });
            }
          }, prefersReducedMotion() ? 0 : 500);
        }
      }
    );
  }

  if (!customElements.get('probieraktion-abschluss')) {
    customElements.define(
      'probieraktion-abschluss',
      class ProbieraktionAbschluss extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('[data-probieraktion-mirror]');
          this.onMode = (event) => this.apply(event.detail.mode, event.detail.href);
          document.addEventListener('probieraktion:mode', this.onMode);
          if (document.querySelector('probieraktion-angebot')) this.apply(state.mode, state.hrefs[state.mode]);
        }

        disconnectedCallback() {
          document.removeEventListener('probieraktion:mode', this.onMode);
        }

        apply(mode, href) {
          setModeVisibility(this, mode);
          if (this.button && href) this.button.setAttribute('href', href);
        }
      }
    );
  }
})();
